import React from 'react';
import { useParams, useHistory } from '../util/router';
import { Card, Row, Col, Badge, Button, Form } from 'react-bootstrap';
import './SampleDetails.css';

const WORKFLOW_OPTIONS = ['Workflow A', 'Workflow B', 'Workflow C'];
const WORKFLOW_MAP = { 'Workflow A': 'WF-A', 'Workflow B': 'WF-B', 'Workflow C': 'WF-C' };

const SAMPLE_DATA = {
  sample_id: 'SMP-2025-000123',
  location_code: 'STA-017',
  group_id: 'RR109-Sept2519',
  batch_id: '2025-Smi-20250919-22944',
  sample_purpose: 'routine',
  priority: 'standard',

  sample_type: 'grab',
  sampler_id: 'EMP-00422',
  sampling_datetime: '2025-09-15T17:35:00',
  field_conditions: 'Rain, 30 mph wind; high H2S warning',
  flow_rate: 24500,

  container_count: 3,
  container_types: '2x1L amber glass; 1x250mL steel bomb',
  storage_location: 'Cold Room A, Shelf 3',

  coc_id: 'COC-2025-09-15-017-01',
  coc_received_datetime: '2025-09-15T19:05:00',
  received_by: 'A. Chen',
  transferred_by: 'R. Nguyen',
  custody_seals: 'Seal #882311, #882312 intact',
  shipping_method: 'courier',

  due_datetime: '2025-09-16T17:35:00',
  tat_target_hr: 24,
  expected_release_datetime: '2025-09-16T15:00:00',
  on_hold_reason: '',

  status: 'received',
  esig_status: 'pending',
  reported_datetime: '2025-09-16T14:22:00',

  photos: 2,
  documents: 3,
  comments: 'Sample taken post-blending',
  attachments: [
    { name: 'CoC_2025-09-15.pdf', href: '#' },
    { name: 'LACT3_Photo_001.jpg', href: '#' },
    { name: 'RVP_Sequence_77.zip', href: '#' }
  ],

  workflows: ['Workflow A', 'Workflow C'],
  workflow_stage: 'interface_cut',
  quality_release_gate: 'pending',
  primary_workflow: 'Workflow A',
  workflows_last_sync: '2025-09-15T19:30:00Z',
  workflows_sync_status: 'in_sync',
  workflows_codes: ['WF-A', 'WF-C'],

  result_id: 'RES-2025-000889',
  test_id: 'TEST-2025-01422',
  analyte: 'Total Sulfur',
  method_code: 'ASTM D4294',
  unit: 'ppm',
  result_value: '108.9',
  spec_min: '',
  spec_max: '100.0',
  spec_status: 'fail',
  analyst_name: 'R. Nguyen',
  analysis_start: '2025-09-15T13:10:00',
  analysis_end: '2025-09-15T13:22:00'
};

const ACTIONS = [
  'Assign to Bench',
  'Enter Data',
  'Approve & Release',
  'Request Re-sample',
  'Place Quality Hold',
  'Export CSV',
  'Generate COA',
  'Create CAPA'
];

function formatValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return `${value.length} item(s)`;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return dateStr.replace('T', ' ').replace('Z', '').substring(0, 16);
}

function getSyncBadgeVariant(status) {
  switch ((status || '').toLowerCase()) {
    case 'in_sync':
    case 'in-sync':
      return 'success';
    case 'stale':
      return 'warning';
    case 'error':
      return 'danger';
    default:
      return 'secondary';
  }
}

function KeyValue({ label, value, className = '' }) {
  return (
    <div className={`key-value-pair ${className}`}>
      <div className="kv-label">{label}</div>
      <div className="kv-value">{value}</div>
    </div>
  );
}

function SectionCard({ title, children, variant = '' }) {
  return (
    <Card className={`section-card ${variant}`}>
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{title}</h5>
          <div>
            <Button variant="outline-secondary" size="sm" className="me-1">
              Guide
            </Button>
            <Button variant="outline-secondary" size="sm" className="me-1">
              History
            </Button>
            <Button variant="outline-secondary" size="sm">
              Docs
            </Button>
          </div>
        </div>
      </Card.Header>
      <Card.Body>{children}</Card.Body>
    </Card>
  );
}

export default function SampleDetails() {
  const { sampleId } = useParams();
  const history = useHistory();

  const sampleData = { ...SAMPLE_DATA, sample_id: sampleId || SAMPLE_DATA.sample_id };

  return (
    <div className="sample-details">
      <div className="details-header mb-4">
        <div>
          <h1>Sample Details</h1>
          <p className="text-muted">Fields rendered from your sheet (inline JSON).</p>
        </div>
        <div className="header-chips">
          <Badge bg="secondary" className="me-1">
            {sampleData.sample_id}
          </Badge>
          <Badge bg="secondary" className="me-1">
            {sampleData.location_code}
          </Badge>
          <Badge bg="secondary" className="me-1">
            {sampleData.status}
          </Badge>
          <Badge bg="secondary">{formatDate(sampleData.due_datetime)}</Badge>
        </div>
      </div>

      <Row>
        <Col lg={6}>
          <SectionCard title="Identification">
            <Row>
              <Col sm={6}>
                <KeyValue label="SAMPLE ID" value={sampleData.sample_id} />
              </Col>
              <Col sm={6}>
                <KeyValue label="LOCATION CODE" value={sampleData.location_code} />
              </Col>
              <Col sm={6}>
                <KeyValue label="GROUP ID" value={sampleData.group_id} />
              </Col>
              <Col sm={6}>
                <KeyValue label="BATCH/LOT ID" value={sampleData.batch_id} />
              </Col>
              <Col sm={6}>
                <KeyValue label="SAMPLE PURPOSE" value={sampleData.sample_purpose} />
              </Col>
              <Col sm={6}>
                <KeyValue label="PRIORITY" value={sampleData.priority} />
              </Col>
            </Row>
          </SectionCard>
        </Col>

        <Col lg={12}>
          <Card className="next-actions-card">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-primary">Next Actions</h5>
                <div>
                  <Button variant="outline-secondary" size="sm" className="me-1">
                    Who can do this?
                  </Button>
                  <Button variant="outline-secondary" size="sm">
                    SLA Policy
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="action-buttons">
                {ACTIONS.map((action, i) => (
                  <Button key={i} variant="outline-secondary" size="sm" className="me-2 mb-2">
                    {action}
                  </Button>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={12}>
          <SectionCard title="Workflow Associations">
            <div className="workflow-section">
              <div className="workflow-chips mb-3">
                {sampleData.workflows.map((wf) => (
                  <Badge key={wf} bg="light" text="dark" className="me-2 workflow-badge">
                    <span className="fw-bold">{wf}</span>
                    <span className="ms-2 text-muted">{WORKFLOW_MAP[wf]}</span>
                  </Badge>
                ))}
              </div>

              <div className="workflow-checkboxes mb-3">
                {WORKFLOW_OPTIONS.map((opt) => (
                  <Form.Check
                    key={opt}
                    inline
                    type="checkbox"
                    label={opt}
                    checked={sampleData.workflows.includes(opt)}
                    disabled
                    className="me-3"
                  />
                ))}
              </div>

              <Row>
                <Col sm={3}>
                  <KeyValue label="PRIMARY WORKFLOW" value={sampleData.primary_workflow} />
                </Col>
                <Col sm={3}>
                  <KeyValue label="WORKFLOW CODES" value={sampleData.workflows_codes.join(', ')} />
                </Col>
                <Col sm={3}>
                  <KeyValue
                    label="SYNC STATUS"
                    value={
                      <Badge bg={getSyncBadgeVariant(sampleData.workflows_sync_status)}>
                        {sampleData.workflows_sync_status}
                      </Badge>
                    }
                  />
                </Col>
                <Col sm={3}>
                  <KeyValue
                    label="LAST SYNC (UTC)"
                    value={formatDate(sampleData.workflows_last_sync)}
                  />
                </Col>
              </Row>

              <Row className="mt-3">
                <Col sm={4}>
                  <KeyValue label="WORKFLOW STAGE" value={sampleData.workflow_stage} />
                </Col>
                <Col sm={4}>
                  <KeyValue label="QUALITY RELEASE GATE" value={sampleData.quality_release_gate} />
                </Col>
                <Col sm={4}>
                  <KeyValue
                    label="DASHBOARD KEY"
                    value={`${sampleData.sample_id}::${sampleData.workflows_codes.join(',')}`}
                  />
                </Col>
              </Row>
            </div>
          </SectionCard>
        </Col>

        <Col lg={6}>
          <SectionCard title="Result (per test/analyte)">
            <Row>
              <Col sm={6}>
                <KeyValue label="RESULT ID" value={sampleData.result_id} />
              </Col>
              <Col sm={6}>
                <KeyValue label="TEST ORDER ID" value={sampleData.test_id} />
              </Col>
              <Col sm={6}>
                <KeyValue label="ANALYTE" value={sampleData.analyte} />
              </Col>
              <Col sm={6}>
                <KeyValue label="METHOD CODE" value={sampleData.method_code} />
              </Col>
              <Col sm={6}>
                <KeyValue label="UNIT" value={sampleData.unit} />
              </Col>
              <Col sm={6}>
                <KeyValue label="RESULT VALUE" value={sampleData.result_value} />
              </Col>
              <Col sm={6}>
                <KeyValue label="SPEC MINIMUM" value={formatValue(sampleData.spec_min)} />
              </Col>
              <Col sm={6}>
                <KeyValue label="SPEC MAXIMUM" value={sampleData.spec_max} />
              </Col>
              <Col sm={6}>
                <KeyValue
                  label="SPEC STATUS"
                  value={
                    <Badge bg={sampleData.spec_status === 'fail' ? 'danger' : 'success'}>
                      {sampleData.spec_status}
                    </Badge>
                  }
                />
              </Col>
              <Col sm={6}>
                <KeyValue label="ANALYST NAME" value={sampleData.analyst_name} />
              </Col>
              <Col sm={6}>
                <KeyValue label="ANALYSIS START" value={formatDate(sampleData.analysis_start)} />
              </Col>
              <Col sm={6}>
                <KeyValue label="ANALYSIS END" value={formatDate(sampleData.analysis_end)} />
              </Col>
            </Row>
          </SectionCard>
        </Col>

        <Col lg={6}>
          <SectionCard title="Sampling">
            <Row>
              <Col sm={6}>
                <KeyValue label="SAMPLE TYPE" value={sampleData.sample_type} />
              </Col>
              <Col sm={6}>
                <KeyValue label="SAMPLER ID" value={sampleData.sampler_id} />
              </Col>
              <Col sm={6}>
                <KeyValue
                  label="SAMPLING DATE/TIME"
                  value={formatDate(sampleData.sampling_datetime)}
                />
              </Col>
              <Col sm={6}>
                <KeyValue label="FLOW RATE" value={sampleData.flow_rate} />
              </Col>
              <Col sm={12}>
                <KeyValue label="FIELD CONDITIONS" value={sampleData.field_conditions} />
              </Col>
            </Row>
          </SectionCard>
        </Col>

        <Col lg={6}>
          <SectionCard title="Container">
            <Row>
              <Col sm={6}>
                <KeyValue label="CONTAINER COUNT" value={sampleData.container_count} />
              </Col>
              <Col sm={6}>
                <KeyValue label="STORAGE LOCATION" value={sampleData.storage_location} />
              </Col>
              <Col sm={12}>
                <KeyValue label="CONTAINER TYPES" value={sampleData.container_types} />
              </Col>
            </Row>
          </SectionCard>
        </Col>

        <Col lg={6}>
          <SectionCard title="CoC">
            <Row>
              <Col sm={6}>
                <KeyValue label="COC ID" value={sampleData.coc_id} />
              </Col>
              <Col sm={6}>
                <KeyValue
                  label="RECEIVED DATE/TIME (LAB)"
                  value={formatDate(sampleData.coc_received_datetime)}
                />
              </Col>
              <Col sm={6}>
                <KeyValue label="RECEIVED BY" value={sampleData.received_by} />
              </Col>
              <Col sm={6}>
                <KeyValue label="TRANSFERRED BY" value={sampleData.transferred_by} />
              </Col>
              <Col sm={6}>
                <KeyValue label="SHIPPING METHOD" value={sampleData.shipping_method} />
              </Col>
              <Col sm={6}>
                <KeyValue label="CUSTODY SEALS LOG" value={sampleData.custody_seals} />
              </Col>
            </Row>
          </SectionCard>
        </Col>

        <Col lg={6}>
          <SectionCard title="Scheduling">
            <Row>
              <Col sm={6}>
                <KeyValue label="DUE DATE/TIME (LAB)" value={formatDate(sampleData.due_datetime)} />
              </Col>
              <Col sm={6}>
                <KeyValue label="TAT TARGET (HRS)" value={sampleData.tat_target_hr} />
              </Col>
              <Col sm={6}>
                <KeyValue
                  label="EXPECTED RELEASE DATE/TIME"
                  value={formatDate(sampleData.expected_release_datetime)}
                />
              </Col>
              <Col sm={6}>
                <KeyValue label="ON HOLD REASON" value={formatValue(sampleData.on_hold_reason)} />
              </Col>
            </Row>
          </SectionCard>
        </Col>

        <Col lg={6}>
          <SectionCard title="Status">
            <Row>
              <Col sm={6}>
                <KeyValue label="STATUS" value={sampleData.status} />
              </Col>
              <Col sm={6}>
                <KeyValue label="BATCH_ID" value={sampleData.batch_id} />
              </Col>
              <Col sm={6}>
                <KeyValue label="E-SIGNATURE STATUS" value={sampleData.esig_status} />
              </Col>
              <Col sm={6}>
                <KeyValue
                  label="REPORTED DATE/TIME"
                  value={formatDate(sampleData.reported_datetime)}
                />
              </Col>
            </Row>
          </SectionCard>
        </Col>

        <Col lg={6}>
          <SectionCard title="Attachments">
            <div className="attachments-section">
              <div className="text-muted mb-2">
                {sampleData.photos} photos • {sampleData.documents} documents
              </div>
              <div className="attachment-links mb-3">
                {sampleData.attachments.map((file, idx) => (
                  <a
                    key={idx}
                    href={file.href}
                    className="btn btn-sm btn-outline-primary me-2 mb-2"
                  >
                    📎 {file.name}
                  </a>
                ))}
              </div>
              {sampleData.comments && (
                <div className="comments">
                  <strong>Comments:</strong> {sampleData.comments}
                </div>
              )}
            </div>
          </SectionCard>
        </Col>
      </Row>

      <div className="mt-4">
        <Button variant="secondary" onClick={() => history.push('/samples')}>
          ← Back to Samples Dashboard
        </Button>
      </div>
    </div>
  );
}
